import {
  useGetActivityMetricsQuery,
  useGetLocationConfigsQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import type { LocationConfigRow } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { locationDisplayName } from 'scenes/RecoveryAI/components/locationDisplayName'
import type { LocationMode } from 'scenes/RecoveryAI/components/LocationModePicker'
import { deriveNewLocationDefault } from 'scenes/RecoveryAI/components/newLocationDefault'
import { selectCurrentMerchantLocations } from 'selectors/location'
import { useWizardState } from '../WizardContext'

// Shared with ActiveLocationsPane/useActiveLocationsController — this stage
// renders the same i18n-agnostic LocationMultiSelect/LocationAddressRow
// components, so the AI-1173 "Unnamed location" fallback lives under the same
// namespace/key rather than duplicating the string under `wizard.locations`.
const t = buildTranslate('recovery_ai.settings.active_locations')

export interface UseWizardLocations {
  /** All selectable locations (drives the multiselect listbox). */
  locations: LocationConfigRow[]
  /** The selected rows, in display order (the removable list). */
  selectedLocations: LocationConfigRow[]
  /** Current all-vs-subset choice (from WizardState). */
  mode: LocationMode
  /** Currently-selected enabled ids (from WizardState). */
  selectedIds: number[]
  /**
   * Total location count M (metrics). Undefined → the call site renders an
   * em-dash, never a fabricated 0. NOTE: M is `merchant_visible` per metrics;
   * the exact "N of M" copy is unreconciled pending AI-208 — do NOT lock it.
   */
  totalCount?: number
  /** Toggle a location in/out of the subset. */
  onToggle: (id: number) => void
  /** Remove a location from the subset by raw-int id. */
  onRemove: (id: number) => void
  /** Switch the all-vs-subset mode. */
  onModeChange: (mode: LocationMode) => void
  /** The location-list GET is in flight. */
  isLoading: boolean
  /** The location-list GET failed. */
  isError: boolean
  /**
   * Persisted merchant-level auto-enroll flag (AI-248, nexus #695) — the baseline
   * LocationsStep diffs against on Save. `undefined` ONLY when the field predates
   * #695 in this env (post-#695 the BE always returns it, false for a legacy
   * merchant); LocationsStep reconciles only when it's defined so a pre-#695
   * backend never gets a doomed, misleading config write.
   */
  newLocationDefaultPersisted: boolean | undefined
  /** The would-be-enabled set Save would persist: every row under "all", else the picked subset. */
  enabledNow: Set<number>
  /** The auto-enroll flag Save would persist (100% coverage collapses to true). */
  newLocationDefault: boolean
  /** True when the picked selection differs from the last-saved per-location state. */
  locationsDirty: boolean
  /** True when the persisted auto-enroll flag differs from newLocationDefault. */
  defaultDirty: boolean
  /** locationsDirty || defaultDirty — an edit Save has not yet persisted. */
  isDirty: boolean
}

/**
 * Controller for the onboarding wizard's Locations stage (AI-161). Reads the
 * per-location enabled state (getLocationConfigs) + total count (metrics),
 * derives the stage's all-vs-subset selection ONCE on first load (opt-in
 * default), and exposes handlers that drive the session-only selection in
 * WizardState (so it survives navigating away from and back to the stage —
 * the provider is mounted above the :step route).
 *
 * The list GET is fetched unconditionally: the wizard runs PRE-activation, so
 * skipping on `onboarding_completed` (as the Settings pane does) would starve
 * the stage of its own list. The GET gate is `view recovery_ai`, not
 * activation — confirmed by the U6 /rai-local smoke.
 *
 * Persistence is NOT here — LocationsStep (U4) owns the dedicated
 * PUT recovery/location_configs save.
 */
export function useWizardLocations(): UseWizardLocations {
  const { state, update } = useWizardState()
  const {
    data: locationConfigs,
    isLoading,
    isError,
  } = useGetLocationConfigsQuery()
  const { data: metrics } = useGetActivityMetricsQuery()
  // Fetched unconditionally (NO skip): the wizard is pre-activation and the GET
  // gate is `view recovery_ai`, not onboarding_completed — skipping would starve
  // LocationsStep of the persisted flag it canonicalizes against.
  const { data: config } = useGetRecoveryConfigQuery()

  const mode = state.locationMode
  const selectedIds = state.selectedLocationIds

  // Latest-value mirror so onToggle/onRemove stay referentially stable
  // (deps: [update]). WizardContext.update takes a partial — NOT a functional
  // updater — so the handlers read the live selection from a ref rather than
  // closing over selectedIds (which would churn their identity on every edit).
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  // AI-1173: mirrors useActiveLocationsController — LocationsStep dispatches
  // getLocations itself (the wizard mounts nowhere near a scene that already
  // loads locations app-wide), so this hook reads the Redux-ORM cache to look
  // up a blank-name row's street address.
  const ormLocations = useSelector(selectCurrentMerchantLocations)
  const ormLocationsById = useMemo(
    () => new Map(ormLocations.map(location => [location.id, location])),
    [ormLocations]
  )

  // AI-1173: mirrors useActiveLocationsController — a blank/null wire name
  // normalizes to '' at the API boundary; resolve the translated "Unnamed
  // location"/"Unnamed location (<address>)" fallback here so neither the
  // multiselect options nor the removable selected-location rows ever render
  // empty.
  const locations = useMemo(
    () =>
      (locationConfigs?.data ?? []).map(location => ({
        ...location,
        name: locationDisplayName(
          location,
          ormLocationsById.get(location.id),
          t
        ),
      })),
    [locationConfigs, ormLocationsById]
  )
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedLocations = useMemo(
    () => locations.filter(l => selectedIdSet.has(l.id)),
    [locations, selectedIdSet]
  )

  // Hydrate the mode + selection from the saved per-location enabled state
  // exactly once (mirrors useWizardHydration's hasHydratedRef gate). "All" only
  // when EVERY row is enabled; otherwise "subset" with the enabled ids (a fresh,
  // opt-in merchant with no rows enabled lands on subset/empty). The once-only
  // ref means a mid-edit refetch can't clobber a user toggle. On re-entry to the
  // stage the hook re-derives from the saved state, mirroring the pane — a
  // reload (which unmounts and remounts this hook) re-reads whatever was last
  // actually persisted; only Back/Cancel (which navigate without persisting,
  // AI-1130) can abandon an edit mid-way, and they're guarded by their own
  // confirm dialog (leaveWouldDiscardEdit in index.tsx).
  const hasHydratedRef = useRef(false)
  useEffect(() => {
    if (!locationConfigs || hasHydratedRef.current) return
    const rows = locationConfigs.data
    const enabledIds = rows.filter(r => r.enabled).map(r => r.id)
    const allEnabled = rows.length > 0 && enabledIds.length === rows.length
    update({
      locationMode: allEnabled ? 'all' : 'subset',
      selectedLocationIds: enabledIds,
    })
    hasHydratedRef.current = true
  }, [locationConfigs, update])

  const onToggle = useCallback(
    (id: number) => {
      const prev = selectedIdsRef.current
      const next = prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
      update({ selectedLocationIds: next })
    },
    [update]
  )

  const onRemove = useCallback(
    (id: number) => {
      update({
        selectedLocationIds: selectedIdsRef.current.filter(x => x !== id),
      })
    },
    [update]
  )

  const onModeChange = useCallback(
    (next: LocationMode) => update({ locationMode: next }),
    [update]
  )

  // Pass presence through (no `?? false`): `undefined` = pre-#695 BE, which
  // LocationsStep uses to skip the doomed flag write (mirrors ActiveLocationsPane).
  const newLocationDefaultPersisted = config?.new_location_default_enabled

  // The would-be-enabled set: every location under "all", else the picked
  // subset. Single source of truth for isDirty AND the Save payload (mirrors
  // ActiveLocationsPane so the shared picker behaves identically on both
  // surfaces).
  const enabledNow = useMemo(
    () => new Set(mode === 'all' ? locations.map(l => l.id) : selectedIds),
    [mode, locations, selectedIds]
  )

  // AI-248/AI-392: onboarding canonicalizes the auto-enroll flag from coverage
  // exactly like the Settings pane (U3) — 100% coverage ⟺ true; a full
  // individual selection collapses to true.
  const newLocationDefault = useMemo(
    () => deriveNewLocationDefault(locations, enabledNow),
    [locations, enabledNow]
  )
  // Reconcile the flag ONLY when the persisted baseline is present: `undefined`
  // means the field predates #695 in this env, where the config PUT no-ops
  // server-side yet reports success and never clears — a false "Saved" that
  // never sticks. Presence-gating fails safe pre-#695 (mirrors
  // ActiveLocationsPane).
  const defaultDirty =
    newLocationDefaultPersisted !== undefined &&
    newLocationDefaultPersisted !== newLocationDefault

  // Dirty when any row's saved enabled state differs from what Save would
  // persist. Pristine forms are non-dirty (this also covers the zero-location
  // merchant: nothing differs ⇒ not dirty).
  const locationsDirty = useMemo(
    () => locations.some(l => l.enabled !== enabledNow.has(l.id)),
    [locations, enabledNow]
  )
  // Combined: a per-location change OR a flag reconciliation.
  const isDirty = locationsDirty || defaultDirty

  // Mirror the stage's unsaved PER-LOCATION edit into shared wizard state.
  // AI-1130: Continue no longer gates on this — it persists the edit itself via
  // continueActionRef instead of requiring a separate Save first. The sole
  // remaining consumer is the wizard-chrome's Back/Cancel "discard changes?"
  // confirm dialog (leaveWouldDiscardEdit in index.tsx), since those paths
  // navigate without persisting.
  //
  // Gate on locationsDirty ONLY, not isDirty: defaultDirty is a reconciliation
  // signal derived purely from hydrated server state (persisted auto-enroll flag
  // vs. coverage) and can be true on first paint with no user edit — e.g. a legacy
  // merchant at full coverage with new_location_default_enabled:false. Treating
  // that as a discardable "unsaved edit" would misrepresent server-derived state.
  // defaultDirty still drives LocationsStep's registered persist action; it just
  // must not surface as an unsaved-edit signal here. Cleanup resets the flag on
  // unmount so it can't outlive the step that owns it (the flag lives in shared
  // wizard state).
  useEffect(() => {
    update({ locationsUnsaved: locationsDirty })
    return () => update({ locationsUnsaved: false })
  }, [locationsDirty, update])

  return {
    locations,
    selectedLocations,
    mode,
    selectedIds,
    totalCount: metrics?.totalLocationsCount,
    onToggle,
    onRemove,
    onModeChange,
    isLoading,
    isError,
    newLocationDefaultPersisted,
    enabledNow,
    newLocationDefault,
    locationsDirty,
    defaultDirty,
    isDirty,
  }
}
